import { NavLink } from 'react-router'
import { useTranslation } from 'react-i18next'
import { APP_VERSIONS, Button } from '@structural-climbing/ui'
import { InstallAppButton } from '../pwa/InstallAppButton'
import { GymMark } from './GymMark'
import { DisciplineGlyph, NavGlyph } from './NavGlyph'
import type { MyGym } from '../gym/useMyGyms'
import { flattenItems, type NavGroup, type NavItem } from './navGroups'
import { PrivacyPolicyLink } from '../legal/PrivacyPolicyLink'

interface Props {
  groups: NavGroup[]
  gymSlug: string | null
  gym: MyGym | null
  canSwitch: boolean
  onSwitchGym: () => void
  badges: { events: number; news: number; competitions: number }
  profileIncomplete: boolean
  onSignOut: () => void
}

function NavBadge({ count }: { count: number }) {
  if (count <= 0) return null
  return (
    <span className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold leading-none text-white">
      {count}
    </span>
  )
}

/** The amber triangle beside Profile while the profile is incomplete. */
function ProfileWarning({ label }: { label: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      className="ml-auto shrink-0 text-warning"
      aria-label={label}
      role="img"
    >
      <path
        d="M8 1.5L14.5 13H1.5L8 1.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M8 6v3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="8" cy="11.5" r="0.75" fill="currentColor" />
    </svg>
  )
}

/**
 * The desktop shell.
 *
 * Grouped rather than flat, mirroring the phone's four tabs, but with every
 * sub-destination left visible — a mouse has no reason to pay for a second tap,
 * and it keeps all nine links one click away.
 */
export function DesktopSidebar({
  groups,
  gymSlug,
  gym,
  canSwitch,
  onSwitchGym,
  badges,
  profileIncomplete,
  onSignOut,
}: Props) {
  const { t } = useTranslation()

  return (
    <aside className="hidden w-64 shrink-0 flex-col border-r border-[var(--gym-line)] bg-content1 p-4 md:flex">
      <div className="mb-5 flex items-center gap-2 pl-2">
        <img src="/icons/symbol-structural.png" alt="Structural" className="h-[30px] w-auto" />
        <img src="/icons/text-structural.png" alt="" className="h-[17px] w-auto" />
      </div>

      {gym && (
        <button
          onClick={canSwitch ? onSwitchGym : undefined}
          aria-label={canSwitch ? t('nav.switchGym') : undefined}
          className={`mb-5 flex items-center gap-2.5 rounded-md p-2 text-left transition-colors ${
            canSwitch ? 'hover:bg-[var(--gym-veil)]' : 'cursor-default'
          }`}
        >
          <GymMark name={gym.name} imageUrl={gym.image_url} size="md" />
          <span className="min-w-0 flex-1 truncate text-sm font-semibold">{gym.name}</span>
        </button>
      )}

      <nav className="flex flex-1 flex-col gap-5 overflow-y-auto">
        {groups.map((group) => {
          const items = group.items(gymSlug)
          if (flattenItems(items).length === 0) return null
          const link = (item: NavItem, nested = false) => (
            <NavLink
              key={item.to}
              to={item.to}
              end
              className={({ isActive }) =>
                `flex items-center gap-1.5 rounded-md py-1.5 text-sm transition-colors ${
                  nested ? 'pl-6 pr-3' : 'px-3'
                } ${
                  isActive
                    ? 'bg-[var(--gym-veil)] font-medium text-foreground'
                    : 'text-foreground/55 hover:text-foreground'
                }`
              }
            >
              {t(item.labelKey)}
              {item.badge && <NavBadge count={badges[item.badge]} />}
              {item.to === '/profile' && profileIncomplete && (
                <ProfileWarning label={t('profile.navWarningLabel')} />
              )}
            </NavLink>
          )
          return (
            <div key={group.id}>
              <p className="eyebrow mb-1.5 flex items-center gap-1.5 px-3 text-foreground/35">
                <NavGlyph group={group.id} className="h-3.5 w-3.5" />
                {t(group.labelKey)}
              </p>
              <div className="flex flex-col gap-0.5">
                {/* A parent with children (Bouldering, Lead) is a label over its
                    own links, so two "Routes" entries never sit side by side
                    without saying which wall they belong to. */}
                {items.map((item) =>
                  item.children ? (
                    <div key={item.to} className="flex flex-col gap-0.5">
                      <p className="flex items-center gap-1.5 px-3 pt-1 text-xs font-medium text-foreground/45">
                        {item.discipline && (
                          <DisciplineGlyph discipline={item.discipline} className="h-3.5 w-3.5" />
                        )}
                        {t(item.labelKey)}
                      </p>
                      {item.children.map((child) => link(child, true))}
                    </div>
                  ) : (
                    link(item)
                  ),
                )}
              </div>
            </div>
          )
        })}
      </nav>

      <InstallAppButton fullWidth className="mb-2 mt-4" />
      <Button size="sm" variant="flat" color="danger" onClick={onSignOut}>
        {t('common.signOut')}
      </Button>
      <p className="mt-2 text-center text-tiny text-foreground/45">v{APP_VERSIONS.member}</p>
      <PrivacyPolicyLink className="mt-1 self-center" />
    </aside>
  )
}
